/**
 * ScenarioIssuesTab
 *
 * Displays automatically-detected issues for a scenario — problems that have
 * arisen due to real-world data changes (forecast refreshes, data source syncs,
 * transaction status changes) that undermine the scenario's assumptions.
 *
 * Each issue offers two resolution paths:
 *   - Dismiss — acknowledged and accepted (risk noted)
 *   - Fix — navigate to the relevant adjustment to update it
 */

import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Info,
  TrendingDown,
  XCircle,
  Clock,
  Database,
  RefreshCw,
  FileText,
  Calendar,
  Repeat,
} from 'lucide-react';
import { useState } from 'react';
import { format } from 'date-fns';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { formatCurrency } from '../../lib/formatting';
import { useTerminology } from '../../hooks/useTerminology';
import type { ScenarioIssue, ScenarioIssueSeverity, ScenarioIssueType } from '../../types/scenarios';

// ── Config ────────────────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<ScenarioIssueSeverity, {
  icon: React.FC<{ className?: string }>;
  label: string;
  badgeClass: string;
  borderClass: string;
  bgClass: string;
  iconClass: string;
}> = {
  critical: {
    icon: XCircle,
    label: 'Critical',
    badgeClass: 'bg-red-100 text-red-700 border-red-200',
    borderClass: 'border-l-red-500',
    bgClass: 'bg-red-50',
    iconClass: 'text-red-500',
  },
  warning: {
    icon: AlertTriangle,
    label: 'Warning',
    badgeClass: 'bg-amber-100 text-amber-700 border-amber-200',
    borderClass: 'border-l-amber-500',
    bgClass: 'bg-amber-50',
    iconClass: 'text-amber-500',
  },
  info: {
    icon: Info,
    label: 'Info',
    badgeClass: 'bg-blue-100 text-blue-700 border-blue-200',
    borderClass: 'border-l-blue-500',
    bgClass: 'bg-blue-50',
    iconClass: 'text-blue-500',
  },
};

const TYPE_CONFIG: Record<ScenarioIssueType, {
  icon: React.FC<{ className?: string }>;
  label: string;
  fixLabel: string;
  fixTab?: string; // tab to navigate to for fixing
}> = {
  'transaction-completed': {
    icon: CheckCircle2,
    label: 'Transaction Settled',
    fixLabel: 'Review Adjustment',
    fixTab: 'builder',
  },
  'transaction-cancelled': {
    icon: XCircle,
    label: 'Transaction Cancelled',
    fixLabel: 'Review Adjustment',
    fixTab: 'builder',
  },
  'transaction-amount-changed': {
    icon: TrendingDown,
    label: 'Amount Changed',
    fixLabel: 'Update Adjustment',
    fixTab: 'builder',
  },
  'base-forecast-changed': {
    icon: RefreshCw,
    label: 'Base Forecast Changed',
    fixLabel: 'Review Impact',
    fixTab: 'impact',
  },
  'data-source-disconnected': {
    icon: Database,
    label: 'Data Source Disconnected',
    fixLabel: 'View Data Sources',
  },
  'start-date-passed': {
    icon: Calendar,
    label: 'Start Date Passed',
    fixLabel: 'Review Scenario',
    fixTab: 'builder',
  },
  'recurring-transaction-amended': {
    icon: Repeat,
    label: 'Recurring Transaction Changed',
    fixLabel: 'Review Adjustment',
    fixTab: 'builder',
  },
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface ScenarioIssuesTabProps {
  issues: ScenarioIssue[];
  currentUserId: string;
  currentUserName: string;
  onDismiss: (issueId: string) => void;
  onFix: (issue: ScenarioIssue) => void;
  readOnly?: boolean;
}

// ── Issue Card ────────────────────────────────────────────────────────────────

const IssueCard: React.FC<{
  issue: ScenarioIssue;
  currentUserName: string;
  onDismiss: (id: string) => void;
  onFix: (issue: ScenarioIssue) => void;
  readOnly: boolean;
}> = ({ issue, currentUserName, onDismiss, onFix, readOnly }) => {
  const [confirming, setConfirming] = useState(false);
  const { getForecastTerm, getScenarioTerm } = useTerminology();
  const severity = SEVERITY_CONFIG[issue.severity];
  const typeConfig = TYPE_CONFIG[issue.type];
  const typeLabel =
    issue.type === 'base-forecast-changed'
      ? `Base ${getForecastTerm(true)} Changed`
      : typeConfig.label;
  const fixLabel =
    issue.type === 'start-date-passed'
      ? `Review ${getScenarioTerm(true)}`
      : typeConfig.fixLabel;
  const SeverityIcon = severity.icon;
  const TypeIcon = typeConfig.icon;
  const isResolved = !!issue.resolution;

  return (
    <div
      className={`rounded-lg border border-l-4 ${severity.borderClass} bg-white shadow-sm overflow-hidden ${isResolved ? 'opacity-60' : ''}`}
    >
      {/* Header row */}
      <div className="flex items-start gap-3 p-4">
        {/* Severity icon */}
        <SeverityIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${severity.iconClass}`} />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-sm text-gray-900">{issue.title}</span>
            <Badge className={`text-xs border ${severity.badgeClass}`}>
              {severity.label}
            </Badge>
            <span className={`inline-flex items-center gap-1 text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-gray-100`}>
              <TypeIcon className="h-3 w-3" />
              {typeLabel}
            </span>
            {isResolved && (
              <Badge className="text-xs border bg-green-50 text-green-700 border-green-200">
                {issue.resolution === 'dismissed' ? 'Dismissed' : 'Fixed'}
              </Badge>
            )}
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed">{issue.description}</p>

          {/* Related adjustment */}
          {issue.relatedChangeName && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <FileText className="h-3 w-3" />
              <span>Related adjustment: <strong className="text-foreground">{issue.relatedChangeName}</strong></span>
            </div>
          )}

          {/* Impact amount */}
          {issue.impactAmount !== undefined && (
            <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${issue.impactAmount < 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              <TrendingDown className={`h-3 w-3 ${issue.impactAmount > 0 ? 'rotate-180' : ''}`} />
              {issue.impactDescription || `${issue.impactAmount < 0 ? '' : '+'}${formatCurrency(issue.impactAmount, 'full')} cash impact`}
            </div>
          )}

          {/* Detected timestamp */}
          <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>Detected {format(issue.detectedAt, 'd MMM yyyy \'at\' HH:mm')}</span>
            {isResolved && issue.resolvedAt && (
              <span className="ml-2">
                · Resolved {format(issue.resolvedAt, 'd MMM yyyy')} by {issue.resolvedBy}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        {!isResolved && !readOnly && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {!confirming ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => setConfirming(true)}
                >
                  Dismiss
                </Button>
                <Button
                  size="sm"
                  className="text-xs h-7 gap-1"
                  onClick={() => onFix(issue)}
                >
                  {fixLabel}
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                <span className="text-xs text-amber-700">Dismiss this issue?</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-6 text-amber-700 hover:bg-amber-100"
                  onClick={() => {
                    onDismiss(issue.id);
                    setConfirming(false);
                  }}
                >
                  Yes, dismiss
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-6"
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

export const ScenarioIssuesTab: React.FC<ScenarioIssuesTabProps> = ({
  issues,
  currentUserId,
  currentUserName,
  onDismiss,
  onFix,
  readOnly = false,
}) => {
  const [showResolved, setShowResolved] = useState(false);
  const { getForecastTerm } = useTerminology();

  const activeIssues = issues.filter(i => !i.resolution);
  const resolvedIssues = issues.filter(i => !!i.resolution);

  const criticalCount = activeIssues.filter(i => i.severity === 'critical').length;
  const warningCount = activeIssues.filter(i => i.severity === 'warning').length;

  if (issues.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-12 w-12 rounded-full bg-green-50 flex items-center justify-center mb-4">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
        </div>
        <h3 className="font-semibold text-gray-900 mb-1">No issues detected</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          These assumptions are consistent with the current base {getForecastTerm(false)} and data sources.
          Issues will appear here automatically if anything changes.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Summary banner */}
      {activeIssues.length > 0 && (
        <div className={`rounded-lg border p-4 ${criticalCount > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-3">
            <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${criticalCount > 0 ? 'text-red-600' : 'text-amber-600'}`} />
            <div>
              <p className={`font-semibold text-sm ${criticalCount > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                {activeIssues.length} issue{activeIssues.length !== 1 ? 's' : ''} require{activeIssues.length === 1 ? 's' : ''} attention
              </p>
              <p className={`text-xs mt-0.5 ${criticalCount > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                {criticalCount > 0 && `${criticalCount} critical${warningCount > 0 ? `, ${warningCount} warning${warningCount !== 1 ? 's' : ''}` : ''}`}
                {criticalCount === 0 && `${warningCount} warning${warningCount !== 1 ? 's' : ''}`}
                {' '}— real-world changes have affected this scenario's assumptions. Review and resolve to maintain accuracy.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Active issues */}
      {activeIssues.length > 0 && (
        <div className="space-y-3">
          {/* Sort: critical first, then warning, then info */}
          {[...activeIssues]
            .sort((a, b) => {
              const order: Record<ScenarioIssueSeverity, number> = { critical: 0, warning: 1, info: 2 };
              return order[a.severity] - order[b.severity];
            })
            .map(issue => (
              <IssueCard
                key={issue.id}
                issue={issue}
                currentUserName={currentUserName}
                onDismiss={onDismiss}
                onFix={onFix}
                readOnly={readOnly}
              />
            ))}
        </div>
      )}

      {/* Resolved issues toggle */}
      {resolvedIssues.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowResolved(v => !v)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${showResolved ? 'rotate-90' : ''}`} />
            {showResolved ? 'Hide' : 'Show'} {resolvedIssues.length} resolved issue{resolvedIssues.length !== 1 ? 's' : ''}
          </button>

          {showResolved && (
            <div className="space-y-3 mt-3">
              {resolvedIssues.map(issue => (
                <IssueCard
                  key={issue.id}
                  issue={issue}
                  currentUserName={currentUserName}
                  onDismiss={onDismiss}
                  onFix={onFix}
                  readOnly={true}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* All resolved state */}
      {activeIssues.length === 0 && resolvedIssues.length > 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="h-10 w-10 rounded-full bg-green-50 flex items-center justify-center mb-3">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
          <p className="font-medium text-sm text-gray-900">All issues resolved</p>
          <p className="text-xs text-muted-foreground mt-1">
            {resolvedIssues.length} issue{resolvedIssues.length !== 1 ? 's' : ''} previously detected and resolved
          </p>
        </div>
      )}
    </div>
  );
};
